import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';

const QUICK_QUESTIONS = [
  '사원의 전화번호를 등록하려면 어느 테이블을 사용해야 하나요?',
  '고객 마스터 정보는 어떤 테이블에 있나요?',
  '판매오더와 자재를 조인하려면 어떤 관계를 써야 하나요?',
  '구매오더 품목의 금액 필드는 어디에 있나요?',
  '회계전표 관련 테이블과 주요 필드를 알려주세요.',
  '자재의 재고수량과 단가 정보는 어느 테이블에 있나요?',
];

const SAP_COLOR = '#0054a6';
const SAP_LIGHT = '#e8f0fe';
const SAP_GRAD = 'linear-gradient(135deg, #003366 0%, #0054a6 50%, #1a73e8 100%)';

function SapChatAgent() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [buildStatus, setBuildStatus] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [tableDetail, setTableDetail] = useState(null);
  const chatEndRef = useRef(null);
  const buildTimerRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/sap-chat/stats`).then(r => r.json()).then(d => setStats(d)).catch(() => {});
    return () => { if (buildTimerRef.current) clearInterval(buildTimerRef.current); };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const q = text || input;
    if (!q.trim() || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: q };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      const res = await fetch(`${API}/sap-chat/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q })
      });
      const data = await res.json();
      setMessages([...newMessages, {
        role: 'assistant',
        content: data.answer || data.error || '응답을 받지 못했습니다.',
        tables_found: data.tables_found || [],
        matched_tags: data.matched_tags || [],
        vector_matches: data.vector_matches || [],
      }]);
    } catch (e) {
      setMessages([...newMessages, { role: 'assistant', content: '서버 연결에 실패했습니다.' }]);
    }
    setLoading(false);
  };

  const startBuild = async () => {
    try {
      await fetch(`${API}/sap-chat/build-vectors`, { method: 'POST' });
      buildTimerRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API}/sap-chat/build-progress`);
          const data = await res.json();
          setBuildStatus(data);
          if (!data.running) {
            clearInterval(buildTimerRef.current);
            buildTimerRef.current = null;
            fetch(`${API}/sap-chat/stats`).then(r => r.json()).then(d => setStats(d)).catch(() => {});
          }
        } catch {}
      }, 3000);
    } catch {}
  };

  const fetchTableDetail = async (tabname) => {
    try {
      const res = await fetch(`${API}/sap-chat/table/${tabname}`);
      const data = await res.json();
      if (!data.error) setTableDetail(data);
    } catch {}
  };

  const [copied, setCopied] = useState(null);
  const handleCopy = (content, idx) => {
    const fallback = (text) => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    };
    try {
      if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(content).catch(() => fallback(content));
      } else {
        fallback(content);
      }
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    } catch (e) {
      fallback(content);
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const mdComponents = {
    h1: ({children}) => <h1 style={{ marginTop: 14, marginBottom: 6, fontSize: 17, color: SAP_COLOR, borderBottom: `2px solid ${SAP_LIGHT}`, paddingBottom: 4 }}>{children}</h1>,
    h2: ({children}) => <h2 style={{ marginTop: 14, marginBottom: 6, fontSize: 16, color: SAP_COLOR }}>{children}</h2>,
    h3: ({children}) => <h3 style={{ marginTop: 12, marginBottom: 6, fontSize: 15, color: SAP_COLOR, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 4, height: 16, background: SAP_COLOR, borderRadius: 2, display: 'inline-block' }}/>{children}
    </h3>,
    strong: ({children}) => <strong style={{ color: '#003366', background: '#f0f6ff', padding: '1px 4px', borderRadius: 3 }}>{children}</strong>,
    ul: ({children}) => <ul style={{ paddingLeft: 18, margin: '6px 0' }}>{children}</ul>,
    ol: ({children}) => <ol style={{ paddingLeft: 18, margin: '6px 0' }}>{children}</ol>,
    li: ({children}) => <li style={{ marginBottom: 4, lineHeight: 1.6 }}>{children}</li>,
    p: ({children}) => <p style={{ margin: '7px 0', lineHeight: 1.7 }}>{children}</p>,
    blockquote: ({children}) => <blockquote style={{ margin: '8px 0', padding: '8px 14px', borderLeft: `3px solid ${SAP_COLOR}`, background: '#f0f6ff', borderRadius: '0 6px 6px 0', fontSize: 13, color: '#003366' }}>{children}</blockquote>,
    code: ({children}) => <code style={{ background: '#f0f4f8', padding: '2px 6px', borderRadius: 4, fontSize: 13, color: '#003366' }}>{children}</code>,
    table: ({children}) => <div style={{ overflowX: 'auto', margin: '8px 0' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>{children}</table></div>,
    th: ({children}) => <th style={{ border: '1px solid #c0d4e8', padding: '7px 10px', background: SAP_LIGHT, textAlign: 'left', fontWeight: 'bold', color: '#003366' }}>{children}</th>,
    td: ({children}) => <td style={{ border: '1px solid #c0d4e8', padding: '7px 10px' }}>{children}</td>,
    hr: () => <hr style={{ border: 'none', borderTop: '1px dashed #c0d4e8', margin: '12px 0' }} />,
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', display: 'flex', flexDirection: 'column' }}>
      {/* 헤더 */}
      <div style={{
        background: SAP_GRAD,
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16,
        boxShadow: '0 2px 12px rgba(0,84,166,0.3)'
      }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}>
            SAP 테이블 검색 챗봇
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.9)' }}>Beta</span>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
            SAP Data Dictionary 기반 테이블/컬럼/관계 검색
            {stats && <span> | {Number(stats.total_tables).toLocaleString()}개 테이블, {Number(stats.total_columns).toLocaleString()}개 컬럼</span>}
          </div>
        </div>
        <button onClick={() => setShowAdmin(!showAdmin)} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
          padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: 4 }}>
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          관리
        </button>
      </div>

      {/* 관리 패널 */}
      {showAdmin && (
        <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '16px 20px' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <h4 style={{ margin: '0 0 12px', color: '#333', fontSize: 14 }}>벡터 인덱스 관리</h4>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              {stats && (
                <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                  <span style={{ background: SAP_LIGHT, padding: '4px 10px', borderRadius: 6 }}>
                    테이블: <b>{Number(stats.total_tables).toLocaleString()}</b>
                  </span>
                  <span style={{ background: SAP_LIGHT, padding: '4px 10px', borderRadius: 6 }}>
                    벡터: <b>{Number(stats.total_vectors).toLocaleString()}</b>
                  </span>
                  <span style={{ background: SAP_LIGHT, padding: '4px 10px', borderRadius: 6 }}>
                    태그: <b>{stats.total_tags}</b>종
                  </span>
                </div>
              )}
              <button onClick={startBuild} disabled={buildStatus?.running}
                style={{
                  padding: '8px 16px', background: buildStatus?.running ? '#ccc' : SAP_COLOR,
                  color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold'
                }}>
                {buildStatus?.running ? '빌드 중...' : '벡터 빌드 시작'}
              </button>
            </div>
            {buildStatus && (buildStatus.running || buildStatus.done > 0) && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                  <div style={{ flex: 1, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: buildStatus.total > 0 ? `${(buildStatus.done / buildStatus.total * 100)}%` : '0%',
                      height: '100%', background: SAP_COLOR, borderRadius: 4, transition: 'width 0.3s'
                    }}/>
                  </div>
                  <span>{buildStatus.done} / {buildStatus.total}</span>
                  {!buildStatus.running && buildStatus.done > 0 && <span style={{ color: '#2e7d32' }}>완료</span>}
                </div>
                {buildStatus.error && <div style={{ color: '#c62828', fontSize: 12, marginTop: 4 }}>{buildStatus.error}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 테이블 상세 모달 */}
      {tableDetail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={e => { if (e.target === e.currentTarget) setTableDetail(null); }}>
          <div style={{ background: 'white', borderRadius: 12, width: '90%', maxWidth: 800, maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: SAP_GRAD, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <span style={{ color: 'white', fontSize: 18, fontWeight: 'bold' }}>{tableDetail.tabname}</span>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginLeft: 10 }}>{tableDetail.desc_ko || tableDetail.desc_en}</span>
              </div>
              <button onClick={() => setTableDetail(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16 }}>X</button>
            </div>
            <div style={{ overflow: 'auto', padding: 24 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ background: SAP_LIGHT, padding: '3px 10px', borderRadius: 12, fontSize: 12 }}>유형: {tableDetail.tabclass}</span>
                <span style={{ background: SAP_LIGHT, padding: '3px 10px', borderRadius: 12, fontSize: 12 }}>딜리버리: {tableDetail.contflag}</span>
                {tableDetail.tags.map(t => (
                  <span key={t} style={{ background: '#e8f5e9', color: '#2e7d32', padding: '3px 10px', borderRadius: 12, fontSize: 12 }}>{t}</span>
                ))}
              </div>

              <h4 style={{ color: SAP_COLOR, margin: '0 0 8px', fontSize: 14 }}>컬럼 ({tableDetail.columns.length}개)</h4>
              <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: SAP_LIGHT }}>
                      <th style={{ border: '1px solid #c0d4e8', padding: '6px 8px', textAlign: 'left' }}>필드명</th>
                      <th style={{ border: '1px solid #c0d4e8', padding: '6px 8px', textAlign: 'left' }}>설명</th>
                      <th style={{ border: '1px solid #c0d4e8', padding: '6px 8px', textAlign: 'center' }}>키</th>
                      <th style={{ border: '1px solid #c0d4e8', padding: '6px 8px', textAlign: 'left' }}>타입</th>
                      <th style={{ border: '1px solid #c0d4e8', padding: '6px 8px', textAlign: 'right' }}>길이</th>
                      <th style={{ border: '1px solid #c0d4e8', padding: '6px 8px', textAlign: 'center' }}>필수</th>
                      <th style={{ border: '1px solid #c0d4e8', padding: '6px 8px', textAlign: 'left' }}>체크테이블</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableDetail.columns.map((c, i) => (
                      <tr key={i} style={{ background: c.key === 'X' ? '#fffde7' : (i % 2 === 0 ? 'white' : '#fafafa') }}>
                        <td style={{ border: '1px solid #e0e0e0', padding: '5px 8px', fontFamily: 'monospace', fontWeight: c.key === 'X' ? 'bold' : 'normal' }}>{c.name}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '5px 8px' }}>{c.desc}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '5px 8px', textAlign: 'center', color: c.key === 'X' ? '#e65100' : '#999' }}>{c.key === 'X' ? 'PK' : ''}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '5px 8px', fontFamily: 'monospace' }}>{c.type}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{c.len}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '5px 8px', textAlign: 'center' }}>{c.mandatory === 'X' ? 'Y' : ''}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '5px 8px', fontFamily: 'monospace', fontSize: 11 }}>
                          {c.checktable && c.checktable !== '*' && (
                            <button onClick={() => fetchTableDetail(c.checktable)}
                              style={{ background: 'none', border: 'none', color: SAP_COLOR, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'monospace', fontSize: 11, padding: 0 }}>
                              {c.checktable}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {tableDetail.refs_out.length > 0 && (
                <>
                  <h4 style={{ color: SAP_COLOR, margin: '0 0 8px', fontSize: 14 }}>참조하는 테이블 (FK)</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {tableDetail.refs_out.map((r, i) => (
                      <button key={i} onClick={() => fetchTableDetail(r.table)}
                        style={{
                          background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6,
                          padding: '4px 10px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4
                        }}>
                        <span style={{ fontFamily: 'monospace', color: '#666' }}>{tableDetail.tabname}.{r.fieldname}</span>
                        <span style={{ color: '#999' }}>&rarr;</span>
                        <span style={{ fontFamily: 'monospace', color: SAP_COLOR, fontWeight: 'bold' }}>{r.table}.{r.forkey}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {tableDetail.refs_in.length > 0 && (
                <>
                  <h4 style={{ color: SAP_COLOR, margin: '0 0 8px', fontSize: 14 }}>이 테이블을 참조하는 테이블</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {tableDetail.refs_in.map((r, i) => (
                      <button key={i} onClick={() => fetchTableDetail(r.table)}
                        style={{
                          background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6,
                          padding: '4px 10px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4
                        }}>
                        <span style={{ fontFamily: 'monospace', color: SAP_COLOR, fontWeight: 'bold' }}>{r.table}</span>
                        <span style={{ color: '#999' }}>&rarr;</span>
                        <span style={{ fontFamily: 'monospace', color: '#666' }}>{tableDetail.tabname}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 채팅 영역 */}
      <div style={{ flex: 1, maxWidth: 900, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', padding: '0 20px' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 0' }}>

          {/* 시작 화면 */}
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div style={{
                width: 72, height: 72, borderRadius: 20,
                background: `linear-gradient(135deg, ${SAP_LIGHT}, #f0f6ff)`,
                margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,84,166,0.15)'
              }}>
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <rect x="4" y="6" width="16" height="14" rx="3" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="2"/>
                  <rect x="28" y="6" width="16" height="14" rx="3" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="2"/>
                  <rect x="16" y="28" width="16" height="14" rx="3" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="2"/>
                  <path d="M20 13h8M12 20v8M36 20v8" stroke={SAP_COLOR} strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 style={{ color: '#333', marginBottom: 6, fontSize: 18 }}>SAP 테이블/컬럼 검색 챗봇</h3>
              <p style={{ color: '#999', fontSize: 14, marginBottom: 6 }}>
                SAP Data Dictionary({stats ? Number(stats.total_tables).toLocaleString() + '개 테이블' : '...'})를 기반으로
              </p>
              <p style={{ color: '#999', fontSize: 14, marginBottom: 24 }}>
                업무에 필요한 테이블, 컬럼, JOIN 관계를 찾아드립니다.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8, maxWidth: 700, margin: '0 auto' }}>
                {QUICK_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)}
                    style={{
                      padding: '12px 14px', background: 'white', border: '1px solid #c0d4e8',
                      borderRadius: 12, cursor: 'pointer', fontSize: 13, color: '#003366',
                      textAlign: 'left', lineHeight: 1.4, transition: 'all 0.15s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = SAP_COLOR; e.currentTarget.style.background = '#f0f6ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#c0d4e8'; e.currentTarget.style.background = 'white'; }}
                  >{q}</button>
                ))}
              </div>
            </div>
          )}

          {/* 메시지들 */}
          {messages.map((msg, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: msg.role === 'assistant' ? 20 : 12,
            }}>
              {msg.role === 'assistant' && (
                <div style={{
                  width: 32, height: 32, borderRadius: 10, background: SAP_LIGHT,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginRight: 10, flexShrink: 0, marginTop: 2
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="8" height="8" rx="2" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="1.5"/>
                    <rect x="13" y="3" width="8" height="8" rx="2" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="1.5"/>
                    <rect x="8" y="13" width="8" height="8" rx="2" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="1.5"/>
                    <path d="M11 7h2M7 11v2M17 11v2" stroke={SAP_COLOR} strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
              )}
              <div style={{ maxWidth: msg.role === 'user' ? '75%' : '88%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: msg.role === 'user' ? '10px 16px' : '16px 20px',
                  borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '2px 18px 18px 18px',
                  background: msg.role === 'user' ? SAP_GRAD : 'white',
                  color: msg.role === 'user' ? 'white' : '#333',
                  border: msg.role === 'user' ? 'none' : '1px solid #c0d4e8',
                  fontSize: 14, lineHeight: 1.7,
                  boxShadow: msg.role === 'user'
                    ? '0 2px 8px rgba(0,84,166,0.25)'
                    : '0 1px 6px rgba(0,0,0,0.06)',
                }}>
                  {msg.role === 'user' ? msg.content : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{msg.content}</ReactMarkdown>
                  )}
                </div>

                {/* 검색된 테이블 태그 */}
                {msg.role === 'assistant' && msg.tables_found && msg.tables_found.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#999', marginRight: 4 }}>검색된 테이블:</span>
                    {msg.tables_found.slice(0, 10).map((t, j) => (
                      <button key={j} onClick={() => fetchTableDetail(t.tabname)}
                        style={{
                          background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 12,
                          padding: '2px 10px', cursor: 'pointer', fontSize: 11,
                          color: SAP_COLOR, fontFamily: 'monospace', fontWeight: 'bold',
                        }}
                        title={`${t.desc} (${t.column_count}개 컬럼)`}
                        onMouseEnter={e => { e.currentTarget.style.background = SAP_LIGHT; e.currentTarget.style.borderColor = SAP_COLOR; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#ddd'; }}
                      >
                        {t.tabname}
                      </button>
                    ))}
                  </div>
                )}

                {/* 액션 바 */}
                {msg.role === 'assistant' && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    <button onClick={() => handleCopy(msg.content, i)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', background: copied === i ? '#e8f5e9' : '#f5f5f5',
                        color: copied === i ? '#2e7d32' : '#555',
                        border: '1px solid ' + (copied === i ? '#a5d6a7' : '#ddd'), borderRadius: 16,
                        cursor: 'pointer', fontSize: 12,
                      }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={copied === i ? '#2e7d32' : '#555'} strokeWidth="2">
                        {copied === i ? <path d="M20 6L9 17l-5-5"/> : <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>}
                      </svg>
                      {copied === i ? '복사됨' : '복사'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* 로딩 */}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 10, background: SAP_LIGHT,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginRight: 10, flexShrink: 0
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="8" height="8" rx="2" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="1.5"/>
                  <rect x="13" y="3" width="8" height="8" rx="2" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="1.5"/>
                  <rect x="8" y="13" width="8" height="8" rx="2" fill={SAP_LIGHT} stroke={SAP_COLOR} strokeWidth="1.5"/>
                </svg>
              </div>
              <div style={{
                padding: '14px 20px', borderRadius: '2px 18px 18px 18px',
                background: 'white', border: '1px solid #c0d4e8',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(n => (
                    <div key={n} style={{
                      width: 8, height: 8, borderRadius: '50%', background: SAP_COLOR,
                      animation: `sapBounce 1.2s ${n * 0.2}s infinite ease-in-out`,
                    }}/>
                  ))}
                </div>
                <span style={{ color: '#003366', fontSize: 13 }}>SAP 테이블 검색 중...</span>
              </div>
              <style>{`@keyframes sapBounce { 0%,80%,100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-8px); opacity: 1; } }`}</style>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* 입력 영역 */}
        <div style={{
          padding: '12px 0 20px', borderTop: '1px solid #c0d4e8',
          display: 'flex', gap: 10, background: '#f5f7fa'
        }}>
          <input type="text" value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !loading) sendMessage(); }}
            placeholder="SAP 테이블, 컬럼, 업무에 대해 질문하세요... (예: 사원 전화번호는 어느 테이블?)"
            style={{
              flex: 1, padding: '12px 18px', borderRadius: 24, border: '1px solid #c0d4e8',
              fontSize: 15, outline: 'none', background: 'white',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
            onFocus={e => e.target.style.borderColor = SAP_COLOR}
            onBlur={e => e.target.style.borderColor = '#c0d4e8'}
          />
          <button onClick={() => sendMessage()} disabled={loading}
            style={{
              width: 48, height: 48, borderRadius: '50%', border: 'none',
              background: loading ? '#ddd' : SAP_GRAD,
              color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: loading ? 'none' : '0 2px 8px rgba(0,84,166,0.3)',
            }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default SapChatAgent;
